import { ReactNode, startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { EMainRoutes } from 'shared/typings';
import {
	addQueryParamsToUrl,
	broadcastChannel,
	broadcastChannelLogoutEvent,
	config,
	extractOneTimeLoginFromHash,
	getKeycloakInstance,
	getOneTimeLoginToken,
	getRedeemToken,
	initKeycloakInstance,
	keycloakInitConfig,
	loginTokenStorage,
	logoutKeycloak,
	ONE_TIME_LOGIN_KEY,
	updateKeycloakToken,
} from 'shared/utils';

import { AuthContext } from './context';
export type { AuthContextType } from './context';
export { AuthContext } from './context';

export interface AuthProviderProps {
	children: ReactNode;
	currentRouteRequiresAuth?: boolean;
	renderWelcomeAuth?: ({ login, register }: { login: () => void; register: () => void }) => ReactNode;
	onAuthorizedChange?: (authorized: boolean) => void;
	onKeycloakInitialized?: () => void;
	locale?: string;
}

let isKeycloakInitializing = false;
let isKeycloakInitialized = false;
const isDevMode = import.meta.env.VITE_ENVIRONMENT === 'dev';

const useOneTimeLoginToken = (currentRouteRequiresAuth?: boolean) => {
	const [isOTl, setIsOTl] = useState<boolean>(!!getOneTimeLoginToken());

	useEffect(() => {
		(async () => {
			if (isOTl) return;

			if (!currentRouteRequiresAuth) {
				setIsOTl(true);
				return;
			}
			const tokenFromHash = extractOneTimeLoginFromHash(location.href);
			const tokenInStorage = getOneTimeLoginToken();
			if (isDevMode && !tokenFromHash && !tokenInStorage) {
				const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
				const loginUrl = `${baseUrl}/${EMainRoutes.ACCOUNT}/${EMainRoutes.LOG_IN}`;
				window.location.replace(loginUrl);
				return;
			}
			if (tokenInStorage) {
				setIsOTl(true);
				return;
			}
			if (tokenFromHash) {
				await getRedeemToken(tokenFromHash);
				setIsOTl(true);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handler = (e: StorageEvent) => {
			if (e.storageArea === loginTokenStorage && e.key === ONE_TIME_LOGIN_KEY) {
				location.reload();
			}
		};
		window.addEventListener('storage', handler);
		return () => window.removeEventListener('storage', handler);
	}, []);

	return isOTl;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useKeycloakRefresher = (enabled = true) => {
	useEffect(() => {
		if (!enabled) return;
		const otlToken = getOneTimeLoginToken();
		const kc = getKeycloakInstance();
		if (!otlToken && kc) {
			const MIN_VALIDITY_SECONDS = 60;
			const REFRESH_BUFFER_SECONDS = 10;
			const refreshIntervalMs = Math.max((MIN_VALIDITY_SECONDS - REFRESH_BUFFER_SECONDS) * 1000, 5 * 1000);

			if (kc.authenticated) {
				updateKeycloakToken();
			}

			const interval = setInterval(() => {
				if (kc.authenticated) updateKeycloakToken();
			}, refreshIntervalMs);

			kc.onTokenExpired = () => updateKeycloakToken();

			return () => {
				clearInterval(interval);
				kc.onTokenExpired = undefined;
			};
		}
	}, [enabled]);
	return { keycloak: getKeycloakInstance() };
};

export const AuthProvider = ({
	children,
	currentRouteRequiresAuth,
	renderWelcomeAuth,
	onAuthorizedChange,
	onKeycloakInitialized,
	locale,
}: AuthProviderProps) => {
	const [authorized, setAuthorized] = useState(false);
	const [gaQueryParam, setGaQueryParam] = useState<string | null | undefined>(null);
	const [registerQueryParam, setRegisterQueryParam] = useState<string | null | undefined>(null);
	const [isShowAuthPage, setIsShowAuthPage] = useState(false);
	const [keycloakInitialized, setKeycloakInitialized] = useState<boolean>(false);
	const isOneTimeLogin = useOneTimeLoginToken(currentRouteRequiresAuth);

	useKeycloakRefresher(!isDevMode);

	const setQueryParams = useEffectEvent(() => {
		const searchParams = new URLSearchParams(location.search);
		setGaQueryParam(searchParams.get('_gl'));
		setRegisterQueryParam(searchParams.get('registration'));
	});

	useEffect(() => {
		setQueryParams();
	}, []);

	const login = useCallback(
		async (redirectUri?: string) => {
			const kc = getKeycloakInstance();
			if (!kc) {
				console.error('Keycloak not initialized');
				return;
			}
			location.href = addQueryParamsToUrl(await kc.createLoginUrl({ redirectUri, locale }), {
				_gl: gaQueryParam,
			});
		},
		[locale, gaQueryParam],
	);

	const register = useCallback(
		async (redirectUri?: string) => {
			const kc = getKeycloakInstance();
			if (!kc) {
				console.error('Keycloak not initialized');
				return;
			}
			location.href = addQueryParamsToUrl(await kc.createRegisterUrl({ redirectUri, locale }), {
				_gl: gaQueryParam,
			});
		},
		[locale, gaQueryParam],
	);

	const logout = useCallback(() => {
		logoutKeycloak();
	}, []);

	useEffect(() => {
		if (keycloakInitialized || isOneTimeLogin || isKeycloakInitializing || isKeycloakInitialized || isDevMode) return;

		isKeycloakInitializing = true;
		const kc = initKeycloakInstance(config);

		kc.onAuthError = () => logoutKeycloak();

		const onBroadcast = (e: MessageEvent) => {
			if (e.data === broadcastChannelLogoutEvent && kc.authenticated) {
				logoutKeycloak({ isUseBroadcast: false });
			}
		};
		broadcastChannel?.addEventListener('message', onBroadcast);

		(async () => {
			const authorizedResult = await kc.init({
				...keycloakInitConfig,
				pkceMethod: window.isSecureContext ? keycloakInitConfig.pkceMethod : undefined,
			});
			startTransition(() => {
				setAuthorized(authorizedResult);
				setKeycloakInitialized(true);
			});
			isKeycloakInitialized = true;
			isKeycloakInitializing = false;
			onKeycloakInitialized?.();
		})();

		return () => {
			broadcastChannel?.removeEventListener('message', onBroadcast);
		};
	}, [keycloakInitialized, currentRouteRequiresAuth, onKeycloakInitialized, isOneTimeLogin]);

	useEffect(() => {
		if (!renderWelcomeAuth && keycloakInitialized && !authorized && currentRouteRequiresAuth) {
			const redirectUrl = new URL(location.href);
			redirectUrl.searchParams.delete('registration');
			if (registerQueryParam) {
				register(redirectUrl.toString());
			} else {
				login();
			}
		}
	}, [
		renderWelcomeAuth,
		keycloakInitialized,
		currentRouteRequiresAuth,
		authorized,
		login,
		register,
		registerQueryParam,
	]);

	useEffect(() => {
		onAuthorizedChange?.(authorized);
	}, [authorized, onAuthorizedChange]);

	const value = useMemo(
		() => ({
			authorized: isOneTimeLogin || authorized,
			logout,
			isShowAuthPage,
			setIsShowAuthPage,
		}),
		[authorized, logout, isShowAuthPage, isOneTimeLogin],
	);

	const Content = useMemo(() => {
		if (!keycloakInitialized && !isOneTimeLogin) return null;
		if (authorized || !currentRouteRequiresAuth || isOneTimeLogin) return children;
		if (!authorized && renderWelcomeAuth) return renderWelcomeAuth({ login, register });
		return null;
	}, [
		authorized,
		children,
		currentRouteRequiresAuth,
		isOneTimeLogin,
		keycloakInitialized,
		login,
		register,
		renderWelcomeAuth,
	]);

	return <AuthContext.Provider value={value}>{Content}</AuthContext.Provider>;
};
